import React, { PropsWithChildren, ReactNode, useEffect, useRef, useState } from 'react';

import Tooltip from 'components/newListeningExperience/shared/Tooltip';
import { getIsOverflowing } from 'hooks/useIsOverflowing';
import { cn } from 'utils/cn';

export interface IButton<T = string> {
  id: T;
  icon?: (props: React.SVGProps<SVGSVGElement>) => JSX.Element;
  text?: ReactNode;
}

interface Props<T extends string> {
  onClick: (id: T, event?: React.MouseEvent<HTMLButtonElement>) => void;
  selected?: T;
  buttons: Array<IButton<T>>;
  className?: string;
  activeBoxClassName?: string;
  iconClassName?: string;
  buttonCustomStyle?: string;
}

const buttonHoverStyle = 'hover:opacity-75';
const buttonFocusStyle = 'focus-visible:ring-2 focus-visible:ring-[#1D76ED]';
const buttonIconDefaultStyle = 'text-icn-txt-sec';
const buttonIconSelectedStyle = 'text-icn-txt-prim';
const buttonStyle = 'p-1.5 rounded-lg bg-transparent outline-none transition-all';

type Offset = {
  left: number;
  width: number;
  height: number;
};

export const LibraryButtonGroup = <T extends string>({
  onClick,
  selected,
  buttons,
  className = '',
  activeBoxClassName = '',
  iconClassName = '',
  buttonCustomStyle = ''
}: PropsWithChildren<Props<T>>) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [selectedItemOffset, setSelectedItemOffset] = useState<Offset>({
    height: 32,
    left: 2,
    width: 60
  });

  useEffect(() => {
    if (!selected) return;
    const selectedEl = ref.current?.querySelector(`#${selected}`);
    if (selectedEl instanceof HTMLElement) {
      setSelectedItemOffset({
        left: selectedEl.offsetLeft,
        height: selectedEl.offsetHeight,
        width: selectedEl.offsetWidth
      });
    }
  }, [selected, buttons]);

  useEffect(() => {
    const buttonEls = ref.current?.querySelectorAll('.button-group-item');
    if (!buttonEls) return;

    buttonEls.forEach(buttonEl => {
      if (buttonEl instanceof HTMLElement && getIsOverflowing(buttonEl, { horizontally: true })) {
        setIsOverflowing(true);
        return;
      }
    });
  }, [setIsOverflowing]);

  return (
    <div
      ref={ref}
      className={cn('w-full p-0.5 bg-bg-sec-0-90 rounded-[10px] justify-center items-center gap-1 inline-flex relative transition-all', className)}
    >
      <div
        className={cn(
          'left-0 absolute h-8 bg-bg-prim-w-70 rounded-[8px] shadow-[0px_2px_4px_0px_rgba(0,0,0,0.08)] transform transition-all',
          activeBoxClassName
        )}
        style={{
          transform: `translateX(${selectedItemOffset?.left}px)`,
          width: selectedItemOffset?.width,
          height: selectedItemOffset?.height
        }}
      />

      {buttons.map(({ id, icon: Icon, text }) => {
        const isSelected = selected === id;
        return (
          <Tooltip content={text} placement="top" show={isOverflowing} key={id}>
            <button
              id={id}
              className={cn(
                'button-group-item overflow-hidden',
                buttonStyle,
                !isSelected && buttonFocusStyle,
                !isSelected && buttonHoverStyle,
                'relative z-10',
                isSelected ? buttonIconSelectedStyle : buttonIconDefaultStyle,
                buttonCustomStyle,
                isOverflowing && 'block text-ellipsis'
              )}
              onClick={event => onClick(id, event)}
              aria-pressed={isSelected}
            >
              {Icon && <Icon className={cn('w-5 h-5', iconClassName, isSelected ? buttonIconSelectedStyle : buttonIconDefaultStyle)} />}
              {text}
            </button>
          </Tooltip>
        );
      })}
    </div>
  );
};
